<script setup>
import { getFileIcon } from '@/configs/files';

defineProps({
  title: {
    type: String,
    default: undefined,
  },
  filterOptions: {
    type: Array,
    default: () => [],
  },
  files: {
    type: Array,
    default: () => [],
  },
  items: {
    type: Array,
    default: () => [],
  },
  itemsPending: {
    type: Boolean,
    default: false,
  },
  getLinkAttributes: {
    type: Function,
    default: () => ({ to: '/' }),
  },
  pageCount: {
    type: Number,
    default: 1,
  },
  linkTitleKey: {
    type: String,
    default: 'title',
  },
  linkDateKey: {
    type: String,
    default: undefined,
  },
  linkGroupKey: {
    type: String,
    default: undefined,
  },
  getGroupTitle: {
    type: Function,
    default: () => 'group',
  },
});

const filterModel = defineModel('filter', { type: String, default: undefined });
const pageModel = defineModel('page', { type: Number, default: 0 });
</script>

<template>
  <div class="bundles-list">
    <div
      v-if="files.length"
      class="bundles-list__links bundles-list__links--files container hidden-desktop"
    >
      <a
        v-for="(file, fileIndex) in files"
        :key="fileIndex"
        :href="file.link"
        target="_blank"
        class="bundles-list__link link"
      >
        <img
          :src="`/images/files/${getFileIcon(file.ext)}.svg`"
          :alt="file.ext"
          class="bundles-list__image bundles-list__image--file"
        />

        <span class="bundles-list__font bundles-list__font--link">
          {{ $tp(file.text) }}
        </span>
      </a>
    </div>

    <CtrSidebar class="bundles-list__sidebar" :title="title" top-line>
      <template #aside>
        <div class="bundles-list__aside">
          <div class="bundles-list__links bundles-list__links--filters">
            <label
              v-for="filterOption in filterOptions"
              :key="filterOption.id"
              class="bundles-list__link link"
              :class="{ 'link--active': filterModel === filterOption.id }"
            >
              <input
                v-model="filterModel"
                type="radio"
                hidden
                :value="filterOption.id"
              />

              <span class="bundles-list__font bundles-list__font--link">
                {{ $tp(filterOption.name) }}
              </span>

              <Transition name="fade">
                <CIcon
                  v-show="filterModel === filterOption.id"
                  name="arrow-left"
                  class="bundles-list__icon bundles-list__icon--arrow hidden-mobile"
                />
              </Transition>
            </label>
          </div>

          <div v-if="files.length" class="bundles-list__links hidden-mobile">
            <a
              v-for="(file, fileIndex) in files"
              :key="fileIndex"
              :href="file.link"
              target="_blank"
              class="bundles-list__link link"
            >
              <img
                :src="`/images/files/${getFileIcon(file.ext)}.svg`"
                :alt="file.ext"
                class="bundles-list__image bundles-list__image--file"
              />

              <span class="bundles-list__font bundles-list__font--link">
                {{ $tp(file.text) }}
              </span>
            </a>
          </div>
        </div>
      </template>

      <template #content>
        <div class="bundles-list__wrapper">
          <template v-if="items.length && !itemsPending">
            <div class="bundles-list__list">
              <template v-for="(item, itemIndex) in items" :key="item.id">
                <template v-if="linkGroupKey">
                  <span
                    v-if="
                      item[linkGroupKey] !==
                      items[itemIndex - 1]?.[linkGroupKey]
                    "
                    class="bundles-list__font bundles-list__font--item"
                  >
                    {{ $tp(getGroupTitle(item)) }}
                  </span>
                </template>

                <NuxtLink
                  class="bundles-list__item link"
                  v-bind="getLinkAttributes(item)"
                >
                  <span class="bundles-list__font bundles-list__font--item">
                    {{ $tp(item[linkTitleKey]) }}
                  </span>

                  <span
                    v-if="linkDateKey"
                    class="bundles-list__font bundles-list__font--date"
                  >
                    {{ $tp(useFormatDate(item[linkDateKey])) }}
                  </span>
                </NuxtLink>
              </template>
            </div>

            <ClientOnly>
              <UiPagination
                v-if="pageCount > 1"
                v-model="pageModel"
                :page-count="pageCount"
              />

              <template #fallback />
            </ClientOnly>
          </template>

          <UiEmptyState v-else-if="!itemsPending" />
        </div>
      </template>
    </CtrSidebar>
  </div>
</template>

<style scoped lang="scss">
.bundles-list {
  $parent: &;

  &__aside {
    margin-top: em(34);
    display: flex;
    flex-direction: column;

    @include media-breakpoint-up(sm) {
      gap: em(93);
    }

    @include media-breakpoint-down(sm) {
      margin: em(9) 0 em(28);
    }
  }

  &__links {
    display: flex;
    gap: em(36);

    @include media-breakpoint-up(sm) {
      flex-direction: column;
      align-items: flex-start;
    }

    &--filters {
      @include media-breakpoint-down(sm) {
        margin: 0 em(-$gap-sm);
        padding: em(10) em($gap-sm);
        gap: em(20);
        overflow: auto hidden;
        @include hide-scroll;
      }
    }

    &--files {
      @include media-breakpoint-down(sm) {
        margin-bottom: em(65);
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }

  &__link {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: em(10);
  }

  &__font {
    &--link {
      font-size: em(18);
      line-height: em(23, 18);
    }

    &--item {
      font-size: em(32);
      line-height: em(35, 32);

      @include media-breakpoint-down(sm) {
        font-size: em(20);
        line-height: em(22, 20);
      }
    }

    &--date {
      color: $color-gray-2;
      font-size: em(18);
      line-height: em(23, 18);
      font-weight: 250;

      @include media-breakpoint-down(sm) {
        font-size: em(16);
        line-height: em(21, 16);
      }
    }
  }

  &__icon {
    &--arrow {
      width: em(11);
      height: em(19);
      transform: scaleX(-1);
    }
  }

  &__image {
    &--file {
      width: em(23);
      height: em(23);
      object-fit: contain;

      @include media-breakpoint-down(sm) {
        width: em(18);
        height: em(18);
      }
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: em(105);

    @include media-breakpoint-down(sm) {
      gap: em(65);
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: em(53);

    @include media-breakpoint-down(sm) {
      gap: em(39);
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: em(18);

    @include media-breakpoint-down(sm) {
      gap: em(14);
    }
  }
}
</style>
